import type { FeatureFlag } from '@/lib/featureAccess'
import axios from 'axios'

import { classifyAuthFailure, resolveAuthRedirect } from '@/lib/authFailure'
import { shouldResyncSession } from '@/lib/featureAccess'

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  timeout: 15000,
  // The auth session is an HttpOnly cookie — it only rides along if the
  // browser is told to send credentials. Same-origin in staging; in dev the
  // API CORS list already allows the SPA origin with allow_credentials=True.
  withCredentials: true,
})

/** The response body's `detail`, or `undefined` when the body has no such field. */
function readDetail(data: unknown): unknown {
  if (typeof data !== 'object' || data === null) return undefined
  return 'detail' in data ? data.detail : undefined
}

/**
 * React to the two failures that mean "this session is over" and re-reject.
 *
 * An expired/invalid session surfaces as a 401 from a protected endpoint: the
 * auth store is cleared and the user bounced to /login rather than left
 * staring at a broken page. A forced password change surfaces as a 403 with
 * detail "password_change_required" from any endpoint (the backend confines
 * such a user to change-password/logout/me); we bounce to /change-password so
 * the confinement is visible in the SPA and not just a silent failed request.
 * Which failure is which — including the exemptions that keep the boot probe
 * and a rejected login out of it — is decided by `classifyAuthFailure`.
 *
 * ⚠️ THE EARLY RETURN ON `'none'` IS LOAD-BEARING, and not for the reason an
 * earlier version of this comment claimed. `@/stores/auth` imports this module
 * STATICALLY (`stores/auth.ts:3`), so client → store is a real cycle and the
 * dynamic `import()` below is the single edge cutting it. The router is not in
 * that cycle at all. What hoisting either import above the classifier would
 * cost is not a static edge but eager instantiation: every 404 the app
 * swallows — a scene lookup, say — would pull the router, and through the
 * store this module, into memory. Keep the imports unreachable for ordinary
 * failures.
 *
 * ⚠️ On a session expiry `clearSession()` runs UNCONDITIONALLY, before the
 * redirect is even resolved. A 401 arriving while the user already sits on
 * /login still wipes the store; it just does not push. Folding the call into
 * the `if (target)` below leaves a dead session in memory on the login screen.
 *
 * Whatever happens, the ORIGINAL error object is re-rejected: every caller's
 * `catch` and every `extractApiError` downstream depends on that identity.
 *
 * Named rather than inlined so stack traces and the spec can refer to it. Not
 * exported: the interceptor below is its only caller, and
 * `api/interceptor.spec.ts` drives it through a real request on purpose, so
 * that the registration is covered too.
 */
async function handleResponseError(error: unknown): Promise<never> {
  if (!axios.isAxiosError(error)) return Promise.reject(error)

  // B10: a stale tab. An admin can switch a module off while the customer has
  // the page open, and the flag list the SPA booted with says they still have
  // it. Re-reading the session on a refusal makes the control disappear on the
  // first click instead of surviving until a reload. Fire-and-forget on purpose:
  // the original rejection must not wait for it, and must not be replaced by it
  // if the probe itself fails.
  // ⚠️ RE-ENTRANCY GUARD, and it is load-bearing rather than defensive. This
  // issues a REQUEST from inside the error handler of a request, so if the
  // probe itself fails in a way that satisfies the predicate, the handler calls
  // itself forever and takes the tab down with it. The URL exclusion in
  // `shouldResyncSession` is supposed to prevent that — and it does in
  // production, where the failing url is the probe's own. It is not enough on
  // its own: MEASURED against tests/api/interceptor.spec.ts, whose adapter
  // rejects every request with one shared error carrying the ORIGINAL url, the
  // exclusion never matches the probe and the recursion is immediate and total
  // (100% CPU, no output, no test ever reported). A flag makes the loop
  // impossible whatever the url says.
  if (
    !resyncInFlight &&
    shouldResyncSession(error.response?.status, error.config?.url ?? '')
  ) {
    resyncInFlight = true
    void import('@/stores/auth')
      .then(({ useAuthStore }) => useAuthStore().syncSession())
      .catch(() => {
        // Swallowed on purpose: this is a best-effort refresh of what the UI
        // may offer, on a path that is ALREADY failing. Letting it reject would
        // replace the caller's error with one about a probe they never made.
      })
      .finally(() => {
        resyncInFlight = false
      })
  }

  const failure = classifyAuthFailure({
    status: error.response?.status,
    url: error.config?.url ?? '',
    detail: readDetail(error.response?.data),
  })
  if (failure === 'none') return Promise.reject(error)

  const { default: router } = await import('@/router')

  if (failure === 'session-expired') {
    const { useAuthStore } = await import('@/stores/auth')
    useAuthStore().clearSession()
  }

  const current = router.currentRoute.value
  const target = resolveAuthRedirect(
    failure,
    // vue-router types a route name as `string | symbol | undefined`; the app
    // only ever uses string names, and the decision module takes no symbols.
    typeof current.name === 'string' ? current.name : undefined,
    current.fullPath,
  )
  if (target) void router.push(target)

  return Promise.reject(error)
}

/**
 * True while a B10 session re-read is in flight. Module scope because the
 * interceptor is registered once per module, and the thing being prevented is
 * one handler invocation triggering the next.
 */
let resyncInFlight = false

apiClient.interceptors.response.use((response) => response, handleResponseError)

// ---------- Geometry ----------

export interface GeoJsonPolygon {
  type: 'Polygon'
  coordinates: number[][][]
}

// ---------- Pond ----------

export interface Pond {
  id: number
  external_id: string
  farm_id: number | null
  status: string
  area_ha: number | null
  geometry: GeoJsonPolygon | null
  notes: string | null
  /** Surroundings-ring width in metres; 0 = off, which is every pond's default. */
  buffer_m: number
  created_at: string
}

export interface PondCreate {
  geometry: GeoJsonPolygon
  external_id?: string
  farm_id?: number | null
  notes?: string | null
}

export interface PondUpdate {
  notes?: string | null
  status?: 'active' | 'archived'
  /** Omit to leave the ring untouched; 0 switches it off. */
  buffer_m?: number
}

// ---------- Alerts ----------

/**
 * Read shape returned by every /api/alerts endpoint.
 *
 * Carries both auto and manual fields. Consumers dispatch on `source`:
 *  - `auto`   → detector-emitted (Wave 2 baseline z-score, Wave 3 thermal).
 *               `z_score` / `score` / `score_type` / `reason` populated;
 *               `description` / `author` / `linked_alert_id` are null.
 *  - `manual` → operator-authored anomaly note. `description` / `author`
 *               populated; the auto-only scoring fields are null.
 *
 * `triggered_at` is a calendar day (YYYY-MM-DD) — the backend stores it
 * as a DATE, not a timestamp.
 */
export interface Alert {
  id: number
  pond_id: number
  level: 'watch' | 'alert' | 'critical'
  indicator: string
  source: 'auto' | 'manual'
  z_score: number | null
  // Wave 3 generic composite score + its type. NULL for Wave 2 baseline
  // alerts (which still populate z_score); set for Wave 3 thermal_risk
  // (score_type='thermal_composite', score on a 0..~1.2 scale).
  score: number | null
  score_type: string | null
  reason: string | null
  /** The sentence a human reads under the numbers. On a manual alert it is the
   *  operator's own text; on an auto alert it is stamped from whatever raised
   *  it — the thermal scoring config (B4) or the threshold rule (B9b) — and
   *  frozen at that moment, so editing the rule later does not rewrite it.
   *  NULL when nothing had anything to add. */
  description: string | null
  /** Session username that authored the manual alert. NULL for auto. */
  author: string | null
  /** Optional pointer at an auto alert on the same pond (manual only). */
  linked_alert_id: number | null
  /** Operator-authored follow-up note. Available on both auto and manual.
   * Either all three set (note + author + timestamp) or all three null. */
  operator_note: string | null
  note_author: string | null
  note_updated_at: string | null
  /** Calendar day, YYYY-MM-DD. */
  triggered_at: string
  resolved_at: string | null
  acknowledged_at: string | null
  acknowledged_by: string | null
  created_at: string
  updated_at: string
}

/**
 * Payload for POST /api/alerts — creates a `source='manual'` row.
 *
 * `author` and `source` are intentionally absent: the backend stamps
 * them (session email + 'manual'). Mirror of `ManualAlertCreate`
 * in api/schemas/alerts.py — keep both sides in sync.
 */
export interface ManualAlertCreate {
  pond_id: number
  /** YYYY-MM-DD (operator-local calendar day). */
  triggered_at: string
  level: 'watch' | 'alert' | 'critical'
  /** Free text — e.g. 'general', 'aerator_fail', 'mortality'. */
  indicator: string
  description: string
  /**
   * Optional confirmation link to an auto alert on the same pond.
   * For POST, `undefined` and `null` are equivalent (backend default
   * is NULL). The semantics only diverge for PATCH — see
   * `ManualAlertUpdate.linked_alert_id`.
   */
  linked_alert_id?: number | null
}

/**
 * Payload for PATCH /api/alerts/{id}. All fields optional.
 *
 * `pond_id` is intentionally not patchable — moving an alert across
 * ponds would silently break chart context. `linked_alert_id`
 * semantics match the backend: omit = leave, null = clear, int = set.
 */
export interface ManualAlertUpdate {
  triggered_at?: string
  level?: 'watch' | 'alert' | 'critical'
  indicator?: string
  description?: string
  linked_alert_id?: number | null
}

export interface AlertListParams {
  pond_id?: number
  days?: number
  level?: 'watch' | 'alert' | 'critical'
  source?: 'auto' | 'manual'
  include_resolved?: boolean
  acknowledged?: boolean
}

// ---------- Alert rules ----------

/**
 * Indicators a threshold rule may name — the twelve this build STORES (B8).
 *
 * Widened from the four optical-water ones: a rule is evaluated by reading a
 * `measurements` column, so an indicator is rule-eligible exactly when it has
 * one. `lst` is the only registry entry left out, because it has none.
 */
export type RuleIndicator =
  | 'ndci'
  | 'ndti'
  | 'dogliotti'
  | 'ndwi'
  | 'fai'
  | 'bda2'
  | 'ndvi'
  | 'savi'
  | 'ndre'
  | 'ndmi'
  | 'nddi'
  | 'iron_oxide'
export type RuleComparator = 'gte' | 'lte'
export type RuleLevel = 'watch' | 'alert' | 'critical'

export interface AlertRule {
  id: number
  pond_id: number
  indicator: RuleIndicator
  comparator: RuleComparator
  threshold: number
  level: RuleLevel
  enabled: boolean
  /** Read-only provenance (B9a). 'template' when the tenant's use-case profile
   *  seeded this rule, null when a person made it — a copy-to-all of a seeded
   *  rule counts as a person. Never sent on create or patch; the API refuses it. */
  origin: 'template' | null
  /** What the operator reads when this rule fires. Copied onto every alert it
   *  raises, so editing them here changes future alerts only. */
  title: string | null
  description: string | null
  created_at: string
  updated_at: string
}

export interface AlertRuleCreate {
  indicator: RuleIndicator
  comparator: RuleComparator
  threshold: number
  level: RuleLevel
  title?: string | null
  description?: string | null
}

export interface AlertRuleUpdate {
  indicator?: RuleIndicator
  comparator?: RuleComparator
  threshold?: number
  level?: RuleLevel
  enabled?: boolean
  /** The only fields a PATCH may set to null: it clears the wording and the
   *  rule falls back to the generated "NDCI 0.42 >= threshold 0.35". */
  title?: string | null
  description?: string | null
}

export interface CopyRulesResult {
  created: number
  skipped: number
  target_ponds: number
}

// ---------- Thermal layer (Wave 3) ----------

export interface ThermalObservation {
  acquired_at: string
  sensor: string
  local_time_of_day: string | null
  t_surface_c: number | null
  t_surface_p90_c: number | null
  pixel_count_valid: number
  pixel_count_total: number
  qc_flags: Record<string, unknown>
  granule_id: string | null
}

export interface WindHourly {
  hour_utc: string
  /**
   * DELIBERATELY DOES NOT LIST `open_meteo_archive`, which the table also
   * stores — and the narrowness is ENFORCED, not merely observed.
   *
   * A reader could exclude archive rows by name. `/weather` cannot: its
   * forecast bucket is whatever won deduplication for hours from now on, and
   * archive OUTRANKS forecast there, so an archive row stamped in the future
   * would surface here. The guarantee is therefore made upstream — the archive
   * ingest drops every hour that has not happened yet, so no such row is ever
   * written. See `fetch_open_meteo_archive`; the provider really does return
   * the rest of the current day, filled from a forecast model.
   */
  source: 'open_meteo' | 'era5'
  wind_speed_ms: number | null
  /**
   * ⚠️ NOT NECESSARILY FROM THE SAME ROW AS THE WIND BESIDE IT. The two values
   * win their hour under separate server-side dedups, so `source`, `om_model`,
   * `forecast_horizon_h` and `era5_product` describe the WIND's provenance
   * only. There is no field describing the temperature's.
   */
  air_temp_c: number | null
  forecast_horizon_h: number | null
  om_model: string | null
  era5_product: string | null
}

export interface ThermalSnapshot {
  pond_id: number
  days: number
  thermal: ThermalObservation[]
}

/**
 * One local calendar day of wind, folded server-side in the FARM's zone so the
 * days line up with `scene_date` on the indicator series drawn beside them.
 *
 * `hours` is how many hours of that day carried a reading. A short count is
 * routine rather than a fault — the first day of a window is clipped, and the
 * freshest day of history arrives with speeds the provider has not filled in
 * yet. When it is 0 the three statistics are null together, which means "no
 * data", not "the wind dropped to zero"; the chart must not join across it.
 */
export interface WindDailyBucket {
  day: string
  mean_ms: number | null
  min_ms: number | null
  max_ms: number | null
  hours: number
}

/**
 * One local day of 2 m air temperature, folded server-side like its wind twin.
 *
 * `hours` counts the hours that carried a READING, and it matters more here
 * than on wind: the ERA5 reanalysis writes rows for these same hours and never
 * carries a temperature, so an hour can exist, be perfectly healthy, and still
 * contribute nothing to this bucket.
 */
export interface AirDailyBucket {
  day: string
  mean_c: number | null
  min_c: number | null
  max_c: number | null
  hours: number
}

export interface PondWeatherSeries {
  pond_id: number
  days: number
  /** The zone BOTH folds ACTUALLY used — `UTC` when the farm's is unusable. */
  timezone: string
  /**
   * TWO LISTS, NOT ONE TABLE WITH TWO COLUMNS. Each is folded from its own
   * server-side deduplication, and those rank the sources differently — the
   * ERA5 reanalysis wins wind hours and is excluded from the temperature race
   * outright, because it never carries a reading. So the day sets need not
   * coincide: a stretch covered by reanalysis alone yields wind days with no
   * temperature day beside them. Do not zip these by index.
   */
  daily_wind: WindDailyBucket[]
  daily_air: AirDailyBucket[]
  forecast: WindHourly[]
}

export interface WeatherCoverage {
  /**
   * Whether a writer holds the lock right now.
   *
   * ONE OF THREE LOCK SPACES, so it also lights up for the nightly forecast
   * ingest — a `true` here is not proof that the operator's own backfill is the
   * thing running.
   */
  is_running: boolean
  /** HOURS of history stored, not rows and not chart points. */
  hours: number
  /**
   * The subset of those hours carrying an air temperature. ALWAYS ≤ `hours`,
   * and a gap between the two is routine: the provider delivers wind ahead of
   * temperature, so the difference is a state of the feed, not a fault.
   */
  air_temp_hours: number
  first_hour_utc: string | null
  last_hour_utc: string | null
}

export interface ThermalRefreshQueued {
  pond_id: number
  queued: string[]
}

export interface ThermalProgress {
  pond_id: number
  running: boolean
  total: number
  done: number
}

// ---------- Indicators / analyze / timeseries ----------

export type IndicatorName =
  | 'ndci' | 'ndti' | 'dogliotti' | 'ndwi' | 'fai' | 'bda2'
  | 'ndvi' | 'savi' | 'ndre' | 'ndmi' | 'nddi' | 'iron_oxide'

export interface IndicesPoint {
  date: string
  ndci?: number | null
  ndti?: number | null
  ndwi?: number | null
  fai?: number | null
  dogliotti?: number | null
  bda2?: number | null
  ndvi?: number | null
  savi?: number | null
  ndre?: number | null
  ndmi?: number | null
  nddi?: number | null
  iron_oxide?: number | null
  cloud_pct?: number | null
  n_pixels?: number | null
  n_pixels_veg?: number | null
  n_pixels_exposed?: number | null
}

export interface AnalyzeMeta {
  scenes_examined: number
  scenes_usable: number
  date_from: string
  date_to: string
  note?: string
}

export interface AnalyzeResponse {
  meta: AnalyzeMeta
  series: IndicesPoint[]
}

export interface TimeseriesEntry {
  date: string
  cloud_pct: number | null
  usable: boolean
  // Task 1 (Scene Preview): linkage to the underlying satellite scene. ALL
  // three fields are optional + nullable so older API responses (or
  // measurements without a matching `scenes` row) keep parsing without
  // changes on the client. The chart enables the "open scene preview"
  // click affordance only when `scene_pk != null`.
  sensor?: string | null
  scene_pk?: number | null
  copernicus_id?: string | null
  ndci?: number | null
  ndti?: number | null
  dogliotti?: number | null
  ndwi?: number | null
  fai?: number | null
  bda2?: number | null
  ndvi?: number | null
  savi?: number | null
  ndre?: number | null
  ndmi?: number | null
  nddi?: number | null
  iron_oxide?: number | null
}

// Task 1: scene preview modal contract. Mirrors `api/schemas/scenes.py`
// — `scene_pk` is the internal DB PK, `copernicus_id` is the wire-level
// Sentinel-2 / Landsat-9 identifier (the S2A_MSIL2A_… / LC09_… string).
// `available=false` means the sensor has no TCI quicklook (Sentinel-1 SAR)
// or STAC could not resolve the asset — the UI renders "Quicklook
// unavailable" instead of a broken <img>.
export interface ScenePreview {
  scene_pk: number
  copernicus_id: string
  sensor: string
  scene_date: string
  cloud_pct: number | null
  thumbnail_url: string | null
  available: boolean
}

export interface TimeseriesEnvelope {
  pond_id: number
  date_from: string
  date_to: string
  series: TimeseriesEntry[]
}

// ---------- Indicators latest ----------

/**
 * Latest per-pond indicator snapshot used to color the map. Optical indices
 * (ndci/ndti/ndwi/fai) come from the most recent usable `measurements` row;
 * `lst` comes from the most recent `pond_thermal_obs` row with a surface temp.
 * Every active pond gets a row — fields are null when no data is available.
 */
export interface PondIndicatorsLatest {
  pond_id: number
  scene_date: string | null
  ndci: number | null
  ndti: number | null
  ndwi: number | null
  fai: number | null
  lst: number | null
  lst_acquired_at: string | null
}

// ---------- Usage ----------

export interface UsageBucket {
  calls: number
  duration_s: number
  errors: number
}

/** One customer's share of the 7-day window. */
export interface TenantUsage {
  tenant_id: string
  slug: string
  name: string
  last_7_days: UsageBucket
}

export interface UsageSummary {
  today: UsageBucket
  last_7_days: UsageBucket
  by_operation: Record<string, number>
  /**
   * WHOSE SPEND IT WAS — the answer the totals cannot give.
   *
   * Ordered by call count, busiest first, and customers with NO calls in the
   * window are included at zero: "spent nothing" and "missing from the report"
   * look identical in a list that drops them, and only one of those is good
   * news.
   *
   * ⚠️ THE SAME 7-DAY WINDOW AS `by_operation`, and NOT the same window as
   * `today`. The three figures sit next to each other and two of them do not
   * add up to the third.
   */
  by_tenant: TenantUsage[]
}

export interface UsageEntry {
  id: number
  /**
   * Whose call this was. The feed is cross-tenant, so without it every row is
   * an anonymous line in a list whose one interesting question is which
   * customer produced it.
   */
  tenant_slug: string
  timestamp: string | null
  operation: string
  pond_id: number | null
  duration_s: number | null
  success: boolean
  n_scenes_examined: number | null
  n_scenes_used: number | null
  error_message: string | null
}

// ---------- Backfill ----------

export interface BackfillQueued {
  pond_id: number
  days: number
  status: string
}

/**
 * One window of a backfill run, mirroring `BackfillChunk` in
 * `api/schemas/ponds.py` (whose authoritative shape is migration
 * `a3d7f14c9b26`).
 *
 * TWO KINDS OF TIME, and reading one as the other produces nonsense.
 * `window_start`/`window_end` are CALENDAR dates — the slice of history asked of
 * GEE, which says nothing about how long the asking takes. `began_at`/`ended_at`
 * are WALL CLOCK instants, and they are the only pair an ETA can be built from.
 * A 365-day window misread as elapsed time looks like a year of work.
 *
 * `scenes_total`/`scenes_done` are OFTEN null even while the chunk genuinely
 * runs: the counter fills them partway in (`scenes_total` arrives one GEE
 * round-trip after the chunk begins). Null means "no denominator yet", never
 * zero.
 */
export interface BackfillChunk {
  /** `YYYY-MM-DD`, inclusive start of the calendar window handed to GEE. */
  window_start: string
  /** `YYYY-MM-DD`, exclusive end — the worker builds it as `start + N days`. */
  window_end: string
  /** ISO 8601 wall-clock instant this chunk started, null before it did. */
  began_at: string | null
  /** ISO 8601 wall-clock instant this chunk stopped, null while it runs. */
  ended_at: string | null
  /**
   * ⚠️ `string`, NOT a union of `'running' | 'done' | 'failed' | 'overdue' |
   * 'skipped'`, and this is a decision rather than a lazy annotation. It mirrors
   * the same deliberate choice on the API side (see the long comment on
   * `BackfillChunk.status` in `api/schemas/ponds.py`): the server refuses to
   * close the set so that a status a newer worker learns to write cannot turn
   * the whole ingest-status payload into a 500.
   *
   * The front end owes the same tolerance, one layer up. Worker, API and SPA
   * ship independently, so this panel WILL at some point poll a running backfill
   * and read a word it has never seen. An unrecognised status must degrade to a
   * neutral chunk — counted, drawn plainly, never dropped and never thrown on —
   * because the moment it would break is precisely the moment the panel exists
   * for: a run in flight. `KNOWN_CHUNK_STATUSES` in `@/lib/ingestProgress` is
   * where the vocabulary this build understands is written down.
   */
  status: string
  scenes_total: number | null
  scenes_done: number | null
  scenes_usable: number | null
  error: string | null
}

/**
 * Where a pond's optical backfill actually is — the progress panel's input.
 *
 * `chunks` is in WRITE ORDER, which is the order `run_backfill` walks windows:
 * NEWEST FIRST, walking back through history. That order is contract — re-sorting
 * it stops describing the run, because "the chunk being worked on" is a position
 * in this sequence. Entries are APPENDED as the walk reaches them, so the array
 * holds the windows started so far, not the whole plan.
 *
 * `window_days` IS NULLABLE AND NULL IS NOT ZERO: it is how many days the run was
 * ASKED for, i.e. the denominator of the progress bar, and this side owns that
 * division. Collapsing null to 0 turns a missing value into a division by zero at
 * the one place it hurts.
 */
export interface BackfillProgress {
  running: boolean
  window_days: number | null
  started_at: string | null
  chunks: BackfillChunk[]
}

export interface IngestStatus {
  is_running: boolean
  scenes_count: number
  last_scene_date: string | null
  last_run_started_at: string | null
  last_run_operation: string | null
  /**
   * ⚠️ null MEANS "NO RUN HAS EVER WRITTEN FOR THIS POND" — there is no row at
   * all. It does NOT mean "a run is in flight and has done nothing yet"; that
   * state arrives as a present object with `running: true` and an empty or
   * partial `chunks`. The two look alike on a progress bar and are opposite
   * answers to the question the panel exists to answer.
   */
  backfill: BackfillProgress | null
}

// ---------- Pond endpoints ----------

export async function fetchPonds(): Promise<Pond[]> {
  const { data } = await apiClient.get<Pond[]>('/api/ponds')
  return data
}

export async function getPond(id: number): Promise<Pond> {
  const { data } = await apiClient.get<Pond>(`/api/ponds/${id}`)
  return data
}

export async function createPond(payload: PondCreate): Promise<Pond> {
  const { data } = await apiClient.post<Pond>('/api/ponds', payload)
  return data
}

export async function updatePond(id: number, payload: PondUpdate): Promise<Pond> {
  const { data } = await apiClient.patch<Pond>(`/api/ponds/${id}`, payload)
  return data
}

export async function deletePond(id: number): Promise<void> {
  await apiClient.delete(`/api/ponds/${id}`)
}

// ---------- Analyze / refresh / backfill ----------

export async function analyzePond(id: number, days = 90): Promise<AnalyzeResponse> {
  const { data } = await apiClient.post<AnalyzeResponse>(
    `/api/ponds/${id}/analyze`,
    null,
    // backend timeout=45s, proxy ~60s — middle ground
    { params: { days }, timeout: 50_000 },
  )
  return data
}

export async function refreshPond(id: number): Promise<AnalyzeResponse> {
  const { data } = await apiClient.post<AnalyzeResponse>(
    `/api/ponds/${id}/refresh`,
    null,
    // backend timeout=45s, proxy ~60s — middle ground
    { timeout: 50_000 },
  )
  return data
}

export async function queueBackfill(id: number, days = 365): Promise<BackfillQueued> {
  const { data } = await apiClient.post<BackfillQueued>(
    `/api/ponds/${id}/backfill`,
    null,
    { params: { days } },
  )
  return data
}

export async function fetchIngestStatus(id: number): Promise<IngestStatus> {
  const { data } = await apiClient.get<IngestStatus>(
    `/api/ponds/${id}/ingest-status`,
  )
  return data
}

export async function fetchIndicatorsLatest(
  farmId?: number,
): Promise<PondIndicatorsLatest[]> {
  const params: Record<string, number> = {}
  if (farmId !== undefined) params['farm_id'] = farmId
  const { data } = await apiClient.get<PondIndicatorsLatest[]>(
    '/api/ponds/indicators-latest',
    { params },
  )
  return data
}

// ---------- Alerts / timeseries ----------

export async function fetchAlerts(
  params: AlertListParams = {},
): Promise<Alert[]> {
  const { data } = await apiClient.get<Alert[]>('/api/alerts', { params })
  return data
}

export async function getAlert(id: number): Promise<Alert> {
  const { data } = await apiClient.get<Alert>(`/api/alerts/${id}`)
  return data
}

export async function createManualAlert(
  payload: ManualAlertCreate,
): Promise<Alert> {
  const { data } = await apiClient.post<Alert>('/api/alerts', payload)
  return data
}

export async function updateManualAlert(
  id: number,
  payload: ManualAlertUpdate,
): Promise<Alert> {
  const { data } = await apiClient.patch<Alert>(`/api/alerts/${id}`, payload)
  return data
}

export async function deleteManualAlert(id: number): Promise<void> {
  await apiClient.delete(`/api/alerts/${id}`)
}

/**
 * Set or clear the operator note on any alert (auto or manual).
 * `null` clears the note + author + timestamp triple; a string sets/replaces.
 * Whitespace-only input is normalised to `null` by the backend.
 */
export async function updateAlertNote(
  id: number,
  note: string | null,
): Promise<Alert> {
  const { data } = await apiClient.patch<Alert>(`/api/alerts/${id}/note`, { note })
  return data
}

export async function fetchAlertRules(pondId: number): Promise<AlertRule[]> {
  const { data } = await apiClient.get<AlertRule[]>(
    `/api/ponds/${pondId}/alert-rules`,
  )
  return data
}

export async function createAlertRule(
  pondId: number,
  payload: AlertRuleCreate,
): Promise<AlertRule> {
  const { data } = await apiClient.post<AlertRule>(
    `/api/ponds/${pondId}/alert-rules`,
    payload,
  )
  return data
}

export async function updateAlertRule(
  ruleId: number,
  payload: AlertRuleUpdate,
): Promise<AlertRule> {
  const { data } = await apiClient.patch<AlertRule>(
    `/api/alert-rules/${ruleId}`,
    payload,
  )
  return data
}

export async function deleteAlertRule(ruleId: number): Promise<void> {
  await apiClient.delete(`/api/alert-rules/${ruleId}`)
}

export async function copyAlertRulesToAll(
  pondId: number,
): Promise<CopyRulesResult> {
  const { data } = await apiClient.post<CopyRulesResult>(
    `/api/ponds/${pondId}/alert-rules/copy-to-all`,
    null,
  )
  return data
}

export async function acknowledgeAlerts(
  alertIds: number[],
): Promise<Alert[]> {
  const { data } = await apiClient.post<Alert[]>('/api/alerts/acknowledge', {
    alert_ids: alertIds,
  })
  return data
}

/** Active auto-alerts for the dashboard banner + per-pond badge. */
export async function fetchActiveAutoAlerts(): Promise<Alert[]> {
  const { data } = await apiClient.get<Alert[]>('/api/alerts', {
    params: {
      source: 'auto',
      acknowledged: false,
      include_resolved: false,
      days: 30,
    },
  })
  return data
}

export interface BaselineSummary {
  pond_id: number
  n_scenes: number
  skipped?: boolean | null
  reason?: string | null
  n_alerts?: number | null
  n_watch?: number | null
  n_alert?: number | null
  n_critical?: number | null
  window_days?: number | null
  z_threshold?: number | null
}

export async function recomputeBaseline(pondId: number): Promise<BaselineSummary> {
  const { data } = await apiClient.post<BaselineSummary>(
    `/api/ponds/${pondId}/baseline`,
  )
  return data
}

export async function fetchTimeseries(
  pondId: number,
  opts: { indicator?: IndicatorName; days?: number } = {},
): Promise<TimeseriesEnvelope> {
  // Critical: do NOT default `indicator` to a single column. The backend
  // narrows the response when an indicator is supplied — only that field
  // ends up in each series entry, the others are absent. The chart needs
  // all four indices, so we omit the param and let the backend inline the
  // full set.
  const { indicator, days = 90 } = opts
  const params: Record<string, string | number> = { days }
  if (indicator) params['indicator'] = indicator
  const { data } = await apiClient.get<TimeseriesEnvelope>(
    `/api/ponds/${pondId}/timeseries`,
    { params },
  )
  return data
}

// ---------- Scene preview (Task 1) ----------

/**
 * Fetch the CDSE STAC thumbnail + metadata for a scene known by its internal
 * PK. Used by `SceneViewer.vue` when the user clicks a chart point that
 * carries `scene_pk` from `/timeseries`.
 *
 * Returns 404-shaped axios error when `scenePk` does not exist; the caller
 * decides whether to surface "Scene unavailable" or just close the modal.
 */
export async function fetchScenePreview(scenePk: number): Promise<ScenePreview> {
  const { data } = await apiClient.get<ScenePreview>(
    `/api/scenes/${scenePk}/preview`,
  )
  return data
}

/**
 * Fallback resolver for entry points that know only (pond, date, sensor) —
 * e.g. an AlertList row click where the scene linkage was never threaded.
 * Returns `null` when no Scene row matches; resolves to a full ScenePreview
 * otherwise. When `sensor` is omitted, the backend prefers S2 → L9 → S1.
 */
export async function fetchSceneByPondDate(
  pondId: number,
  date: string,
  sensor?: string,
): Promise<ScenePreview | null> {
  const params: Record<string, string | number> = { pond_id: pondId, date }
  if (sensor) params['sensor'] = sensor
  try {
    const { data } = await apiClient.get<ScenePreview>(
      '/api/scenes/by-pond-date',
      { params },
    )
    return data
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) return null
    throw err
  }
}

// ---------- Usage ----------

export async function fetchUsageSummary(): Promise<UsageSummary> {
  const { data } = await apiClient.get<UsageSummary>('/api/usage/summary')
  return data
}

export async function fetchUsageRecent(limit = 20): Promise<UsageEntry[]> {
  const { data } = await apiClient.get<UsageEntry[]>('/api/usage/recent', {
    params: { limit },
  })
  return data
}

// ---------- Provider allocation (A9-a) ----------

/** One bucket of metered spend: a provider AND the unit it bills in. */
export interface ProviderUsage {
  provider: string
  /**
   * What the provider counts — null when it bills per call and nothing finer
   * was ever measured. A null here makes `billable_units` meaningless; read
   * `calls` instead.
   */
  units_kind: string | null
  billable_units: number
  calls: number
  /**
   * Percentage OF THIS BUCKET, not of the installation's spend.
   *
   * ⚠️ SHARES DO NOT ADD UP ACROSS PROVIDERS. Each bucket carries its own 100%,
   * so one customer's percentages across a row sum to whatever they sum to —
   * a row total is not a number that means anything, and a stacked bar drawn
   * from these would be a lie with axes.
   */
  share_pct: number
  /**
   * Calls that landed in this bucket with NO measured quantity.
   *
   * The share was computed from the rest. Above zero, the percentage is a
   * lower bound on the truth and the screen has to say so — a share quietly
   * derived from a subset is the failure mode that makes a report worse than
   * no report.
   */
  calls_missing_units: number
}

/** One customer's slice of the window, bucket by bucket. */
export interface TenantAllocation {
  tenant_id: string
  slug: string
  name: string
  calls: number
  per_provider: ProviderUsage[]
}

export interface AllocationReport {
  window_from: string
  window_to: string
  /** Installation totals per bucket; `share_pct` is 100 by construction. */
  totals: ProviderUsage[]
  /**
   * Largest first, and customers with NO calls are included at zero — same
   * argument as `UsageSummary.by_tenant`: "spent nothing" and "missing from
   * the report" look identical in a list that drops them.
   */
  by_tenant: TenantAllocation[]
  /**
   * Rows the meter could not attribute and threw away.
   *
   * ⚠️ COUNTED PER PROCESS, so with several workers this UNDERSTATES the loss.
   * Above zero the report is incomplete, and every percentage on it is drawn
   * from an incomplete denominator.
   */
  dropped_rows: number
}

/**
 * The per-customer provider allocation over an explicit window.
 *
 * `from`/`to` are ISO-8601 instants, not calendar days: the caller decides the
 * window (see `lib/allocation.monthWindow`), because "this month" is a local
 * -calendar question the API has no business guessing.
 */
export async function fetchAllocation(
  from: string,
  to: string,
): Promise<AllocationReport> {
  const { data } = await apiClient.get<AllocationReport>('/api/usage/allocation', {
    params: { from, to },
  })
  return data
}

// ---------- Heatmap (Task 9) ----------

/** Heatmap indicators available for spectral-index rendering. Excludes `lst` (Landsat thermal). */
export type HeatmapIndicator =
  | 'ndci'
  | 'ndti'
  | 'ndwi'
  | 'fai'
  | 'ndvi'
  | 'ndre'
  | 'ndmi'
  | 'nddi'

export interface HeatmapRequest {
  /** [west, south, east, north] in EPSG:4326. */
  bbox: [number, number, number, number]
  indicator: HeatmapIndicator
  /** YYYY-MM-DD calendar day, or null to use the most recent available scene. */
  date: string | null
  threshold: number | null
  /** Colour ramp; viridis/cividis are colour-blindness safe. */
  palette?: HeatmapPalette
}

export type HeatmapPalette = 'blue-red' | 'viridis' | 'cividis'

export interface HeatmapResponse {
  /** PNG bytes encoded as base64 (no data-URI prefix). */
  png_base64: string
  /** [[south, west], [north, east]] — matches L.LatLngBoundsExpression. */
  bounds: [[number, number], [number, number]]
  /** Actual acquisition date of the rendered scene (YYYY-MM-DD). */
  scene_date: string
}

/**
 * Request a colourised spectral-index heatmap PNG for the given bbox, indicator,
 * and date. Long-running — backend does a rasterio windowed read + reproject.
 * Throws AxiosError with status 404 when no Sentinel-2 scene covers the date,
 * 422 when the bbox or indicator is invalid, and 502 when scenes DO cover it and
 * none of them could be read — most often a refused CDSE credential.
 *
 * 404 and 502 are not interchangeable and the UI must not merge them: 404 means
 * pick another date, 502 means the date is fine and our access is not. See
 * `lib/heatmapError.ts`, which owns the sentence each one produces.
 */
export async function computeHeatmap(req: HeatmapRequest): Promise<HeatmapResponse> {
  const { data } = await apiClient.post<HeatmapResponse>(
    '/api/heatmap/render',
    req,
    // Raster read + warp can take 15–30 s on cold CDSE S3 paths.
    { timeout: 60_000 },
  )
  return data
}

// ---------- Current imagery (basemap) ----------

export interface CurrentImageryResponse {
  /** PNG bytes encoded as base64 (no data-URI prefix). */
  png_base64: string
  /** [[south, west], [north, east]] — matches L.LatLngBoundsExpression. */
  bounds: [[number, number], [number, number]]
  /** Newest and oldest Sentinel-2 pass that contributed pixels (YYYY-MM-DD). */
  newest_date: string
  oldest_date: string
  /** Share of the overlay that is filled, 0..1 — the rest was cloud. */
  clear_fraction: number
}

/**
 * Latest cloud-free Sentinel-2 true colour for a viewport (10 m). Not behind
 * the heatmap module: it is part of drawing a pond.
 * 404 = clouds / no pass in 5 weeks; 502 = imagery exists and we cannot read it.
 */
export async function fetchCurrentImagery(
  bbox: [number, number, number, number],
): Promise<CurrentImageryResponse> {
  const [west, south, east, north] = bbox
  const { data } = await apiClient.get<CurrentImageryResponse>('/api/basemap/current', {
    params: { west, south, east, north },
    timeout: 60_000,
  })
  return data
}

// ---------- Weather ----------
//
// Its own section, not a member of the thermal one below, because that is the
// whole point: this route is gated on the `weather` module while every thermal
// endpoint sits behind `require_feature(THERMAL)`. Filing it under "Thermal"
// would put the reader one heading away from the wrong conclusion — the two
// modules are sold separately, and either can be off while the other is on.

/**
 * Weather for one pond: wind history + the 48 h ahead.
 *
 * Behind the `weather` module, NOT the thermal one that `fetchThermalSnapshot`
 * below sits under. That separation is the entire reason this route exists:
 * wind used to arrive on the thermal response and vanished with a module it
 * has nothing to do with. It answers 403 without `weather`, so callers gate on
 * that flag before asking.
 */
export async function fetchPondWeather(
  pondId: number,
  days = 180,
): Promise<PondWeatherSeries> {
  const { data } = await apiClient.get<PondWeatherSeries>(
    `/api/ponds/${pondId}/weather`,
    { params: { days } },
  )
  return data
}

/**
 * How much weather history this pond holds.
 *
 * Behind the same `weather` module as `fetchPondWeather`, so callers gate on
 * that flag before asking — a tenant without the module gets 403, and the SPA
 * re-probes its session on every one of those.
 */
export async function fetchWeatherCoverage(pondId: number): Promise<WeatherCoverage> {
  const { data } = await apiClient.get<WeatherCoverage>(
    `/api/ponds/${pondId}/weather/coverage`,
  )
  return data
}

// ---------- Thermal (Wave 3) ----------

export async function fetchThermalSnapshot(
  pondId: number,
  days = 14,
): Promise<ThermalSnapshot> {
  const { data } = await apiClient.get<ThermalSnapshot>(
    `/api/ponds/${pondId}/thermal`,
    { params: { days } },
  )
  return data
}

export async function refreshThermal(pondId: number): Promise<ThermalRefreshQueued> {
  const { data } = await apiClient.post<ThermalRefreshQueued>(
    `/api/ponds/${pondId}/thermal/refresh`,
  )
  return data
}

export async function fetchThermalProgress(
  pondId: number,
): Promise<ThermalProgress> {
  const { data } = await apiClient.get<ThermalProgress>(
    `/api/ponds/${pondId}/thermal/progress`,
  )
  return data
}

// ---------- Auth ----------

export type Role = 'owner' | 'admin' | 'operator' | 'viewer'

/**
 * Who is logged in, from the backend's point of view. `email` is the login
 * identifier (server-side sessions, SP1a); `must_change_password` is true for a
 * bootstrap/invited account that has never set its own password — the UI then
 * confines the user to the change-password screen.
 */
/** One indicator the tenant's use-case profile makes visible (B5). */
export interface ProfileIndicator {
  key: string
  /** What the operator reads when this indicator moves; `null` = say nothing
   * special and keep the UI's own generic wording. */
  anomaly_high_text: string | null
  anomaly_low_text: string | null
}

/** The use-case profile, reduced to what a customer may know (B5). */
export interface SessionProfile {
  indicators: ProfileIndicator[]
}

export interface SessionInfo {
  email: string
  role: Role
  must_change_password: boolean
  /**
   * The product modules this session has (B10) — flag keys, catalogue order,
   * only the ones that are ON.
   *
   * TYPED AS THE UNION, not `string[]`: the backend emits an enum in its schema
   * precisely so a misspelt key fails to compile here rather than silently
   * hiding a module the customer paid for.
   *
   * EFFECTIVE, like every other field: inside an impersonation window this is
   * the CUSTOMER's list, because that is what the API will enforce.
   */
  features: FeatureFlag[]
  /**
   * Which indicators this session may see (B5), in the order the tenant's
   * use-case profile prescribes.
   *
   * A SECOND SWITCH, NOT A REPLACEMENT for `features`: `lst` needs the
   * `thermal` module AND a profile that lists it. A shrimp farm and a tuna farm
   * on identical modules see different indicators.
   *
   * `key` IS `string`, NOT `FilterIndicator`: the profile is data a platform
   * admin edits and may name an indicator this build has retired. The
   * intersection with the frontend's universe happens in `indicatorFilter`.
   *
   * No profile `key`/`label` on the wire, so nothing here can branch on
   * "is this a shrimp farm".
   */
  profile: SessionProfile
  /**
   * The tenant is suspended: reads work, every write is refused with a 403.
   *
   * Arrives on the session rather than from a separate call on purpose — a
   * banner that appears one request late teaches the reader that its absence
   * means nothing.
   */
  tenant_suspended: boolean
  /**
   * This session may administer the PLATFORM — our screens onto the whole
   * installation, not a rung on any customer's role ladder. The two axes are
   * disjoint: a platform account's `role` is whatever it happens to be (the CLI
   * creates it as `viewer`), and no amount of rank on the ladder produces this
   * flag.
   *
   * ⚠️ FALSE WHILE IMPERSONATING, and that is not a quirk to work around. Inside
   * a window the backend resolves the caller to the CUSTOMER's principal, so
   * every platform-only route answers 403 — and this flag says so, which is what
   * keeps the navigation honest about what the next click will do.
   */
  is_platform_admin: boolean
  /**
   * The customer this session is currently acting as, or null. A NAME, because
   * its only consumer is a banner a person reads.
   *
   * Paired with `impersonation_expires_at`: both are set, or neither is.
   */
  impersonated_tenant_name: string | null
  /** When the impersonation window closes on its own (ISO-8601), or null. */
  impersonation_expires_at: string | null
  /**
   * The tenant every gate decides with: the customer's inside an impersonation
   * window, the account's own otherwise, null for a tenant-less platform
   * session. The auth store watches it change to drop every tenant-scoped
   * store at once — see `applySession`.
   */
  effective_tenant_id: string | null
  /**
   * Where the pond map opens for the EFFECTIVE tenant, or null (world view).
   * On the session for the reason `features` is: it must arrive with the
   * identity, not one request later, or the map flashes the wrong place.
   */
  map_region: MapRegion | null
}

/**
 * A map start view set by the platform admin per tenant. Mirrors the backend's
 * `api/schemas/map_region.py`: all three fields or no region at all.
 */
export interface MapRegion {
  lat: number
  lon: number
  zoom: number
}

/** Verify credentials; on success the backend sets the HttpOnly session cookie. */
export async function login(
  email: string,
  password: string,
): Promise<SessionInfo> {
  const { data } = await apiClient.post<SessionInfo>('/api/auth/login', {
    email,
    password,
  })
  return data
}

/** Clear the server-side session cookie. Unguarded — always succeeds (204). */
export async function logout(): Promise<void> {
  await apiClient.post('/api/auth/logout')
}

/**
 * Boot probe for login state. The session cookie is HttpOnly so JS cannot
 * read it directly — this endpoint is the only way to learn who is logged in.
 * Resolves with the session on success; rejects with a 401 when logged out.
 */
export async function fetchSession(): Promise<SessionInfo> {
  const { data } = await apiClient.get<SessionInfo>('/api/auth/me')
  return data
}

/**
 * Change the caller's password. Requires a live session. The backend rotates
 * the session cookie and returns the refreshed session (must_change_password
 * now false), so the store can lift the forced-change confinement.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<SessionInfo> {
  const { data } = await apiClient.post<SessionInfo>(
    '/api/auth/change-password',
    { current_password: currentPassword, new_password: newPassword },
  )
  return data
}

/**
 * Request a password-reset link. Always resolves (the backend answers with a
 * constant 202 whether or not the address has an account), so the UI must
 * reveal nothing either way — same message for every input.
 */
export async function forgotPassword(email: string): Promise<void> {
  await apiClient.post('/api/auth/forgot-password', { email })
}

/**
 * Spend a reset token and set a new password. Rejects with 400 when the token
 * is unknown, expired, or already used.
 */
export async function resetPassword(
  token: string,
  newPassword: string,
): Promise<void> {
  await apiClient.post('/api/auth/reset-password', {
    token,
    new_password: newPassword,
  })
}

// ---------- Annotations ----------

/**
 * Operator-recorded event types. The string union mirrors the backend enum;
 * keep both sides in sync (changing here without changing the API/CHECK
 * constraint will yield 422 on create).
 */
export type EventType =
  | 'storm'
  | 'cleaning'
  | 'seeding'
  | 'inspection'
  | 'harvest'
  | 'feeding_change'
  | 'disease'
  | 'other'

export interface Annotation {
  id: number
  pond_id: number
  event_type: EventType
  description: string
  author: string
  /** ISO 8601 with TZ, e.g. "2026-05-20T20:30:00+00:00". */
  occurred_at: string
  created_at: string
  updated_at: string
}

export interface AnnotationCreate {
  pond_id: number
  event_type: EventType
  description: string
  /** Caller is responsible for ensuring this is a UTC ISO 8601 string. */
  occurred_at: string
}

export interface AnnotationUpdate {
  event_type?: EventType
  description?: string
  occurred_at?: string
}

export interface AnnotationListParams {
  pond_id?: number
  event_type?: EventType
  /** Inclusive lower bound on occurred_at, YYYY-MM-DD (operator-local). */
  date_from?: string
  /** Inclusive upper bound on occurred_at, YYYY-MM-DD (operator-local). */
  date_to?: string
  limit?: number
}

export async function fetchAnnotations(
  params: AnnotationListParams = {},
): Promise<Annotation[]> {
  const { data } = await apiClient.get<Annotation[]>('/api/annotations', {
    params,
  })
  return data
}

export async function getAnnotation(id: number): Promise<Annotation> {
  const { data } = await apiClient.get<Annotation>(`/api/annotations/${id}`)
  return data
}

export async function createAnnotation(
  payload: AnnotationCreate,
): Promise<Annotation> {
  const { data } = await apiClient.post<Annotation>(
    '/api/annotations',
    payload,
  )
  return data
}

export async function updateAnnotation(
  id: number,
  payload: AnnotationUpdate,
): Promise<Annotation> {
  const { data } = await apiClient.patch<Annotation>(
    `/api/annotations/${id}`,
    payload,
  )
  return data
}

export async function deleteAnnotation(id: number): Promise<void> {
  await apiClient.delete(`/api/annotations/${id}`)
}

// ---------- Reports (Task 7: PDF export) ----------

/**
 * Indicator keys accepted by `POST /api/reports`. Mirrors the
 * `Literal[...]` tuple in `api/schemas/reports.py` — keep both sides in
 * sync; the backend uses `extra='forbid'` and rejects unknown values.
 *
 * Note: this is intentionally NOT the same as `FilterIndicator` from
 * `lib/indicatorScale.ts` — that one drives the map color picker (which
 * also includes `lst`) while reports cover the optical indices that the
 * `measurements` table populates per scene.
 */
export type ReportIndicator =
  | 'ndci'
  | 'ndti'
  | 'dogliotti'
  | 'ndwi'
  | 'fai'
  | 'bda2'

/**
 * Request body for `POST /api/reports`. Mirrors `ReportRequest` in
 * `api/schemas/reports.py`. Field names use snake_case to match the wire
 * format; do not rename without updating the backend schema.
 */
export interface ReportRequest {
  /** Inclusive lower bound, YYYY-MM-DD (operator-local calendar day). */
  date_from: string
  /** Inclusive upper bound, YYYY-MM-DD. Backend enforces `date_from <= date_to`. */
  date_to: string
  /** 1..50 pond ids; backend 422s on empty / over-limit. */
  pond_ids: number[]
  /** 1..6 indicators; backend 422s on empty / over-limit. */
  indicators: ReportIndicator[]
  include_alerts: boolean
  include_notes: boolean
}

/**
 * Download the generated PDF as a `Blob`. The backend serves bytes from
 * memory with two headers we care about:
 *   - `Content-Disposition: attachment; filename="..."`
 *   - `X-Filename: ...` (lowercased by axios → `x-filename`)
 *
 * We read the dedicated `X-Filename` header to avoid parsing
 * Content-Disposition (RFC 5987/6266 quoting is fiddly); fall back to a
 * generic filename when the header is absent. Non-2xx responses re-throw
 * so callers can surface a user-facing message via `extractApiError`.
 */
export async function fetchReportPdf(
  req: ReportRequest,
): Promise<{ blob: Blob; filename: string }> {
  const response = await apiClient.post<Blob>('/api/reports', req, {
    responseType: 'blob',
    // PDF render can take a while for wide ranges; backend hard-caps at 60s.
    // Mirror that ceiling on the client so a transient backend timeout
    // surfaces as a clean error rather than an axios-default 15s abort.
    timeout: 65_000,
  })
  const headerName = response.headers['x-filename']
  const filename =
    typeof headerName === 'string' && headerName.length > 0
      ? headerName
      : 'eoscope-report.pdf'
  return { blob: response.data, filename }
}

// ---------- User administration (batch 4: the /team screen) ----------

/**
 * One row of `GET /api/users`. Mirrors `UserListItem` in
 * `api/schemas/users.py`; field names are the wire's snake_case.
 *
 * ⚠️ NO `initial_password` HERE, AND THAT ABSENCE IS THE CONTRACT. The backend
 * keeps the creation response in a separate model for exactly this reason: an
 * optional secret on the list type would make `user.initial_password` compile
 * everywhere, so a template that renders it looks correct right up until the
 * day a row actually carries one. `UserCreated` below extends this type, never
 * the other way round.
 *
 * `is_self` is shipped by the server rather than derived here. The rule it
 * feeds ("nobody administers their own row") belongs to
 * `api/auth/user_admin_policy.py`; re-deriving it in the SPA means matching the
 * server's email normalisation, and the two derivations disagreeing shows up as
 * an enabled button that answers 403.
 */
export interface UserListItem {
  /** UUID, not the integer ids used elsewhere in this file. */
  id: string
  email: string
  role: Role
  is_active: boolean
  /** ISO-8601 timestamp of account creation. */
  created_at: string
  /** True on the calling admin's own row: it offers no administrative action. */
  is_self: boolean
}

/**
 * Response of `POST /api/users` — the created row plus the one-time password.
 *
 * 🔴 `initial_password` IS THE ONLY PASSWORD THIS API EVER RETURNS, and it is
 * returned exactly once: only an Argon2 hash is stored, so the value cannot be
 * fetched again by anyone, including us. Whatever renders it must therefore
 * treat closing the dialog as destroying it — there is no mail delivery in this
 * product (`core/email/` ships a `sink` adapter only), so a lost password is a
 * phone call and a reset, not a resend.
 *
 * Never log it, never put it in a store that survives the dialog, never send it
 * to an error reporter.
 */
export interface UserCreated extends UserListItem {
  initial_password: string
}

/**
 * Everyone in the caller's tenant, INCLUDING deactivated accounts.
 *
 * Deactivated rows are not filtered out client-side: `/team` is the screen on
 * which an account gets switched back on, and a list that hides them offers no
 * way back. They are marked, not removed.
 */
export async function listUsers(): Promise<UserListItem[]> {
  const { data } = await apiClient.get<UserListItem[]>('/api/users')
  return data
}

/**
 * Provision a colleague. The server picks the password and the tenant; the
 * caller supplies an address and a role and nothing else (`extra="forbid"`
 * turns any smuggled `tenant_id` / `is_platform_admin` into a 422).
 *
 * 201 with a body that carries the secret — see `UserCreated`.
 */
export async function createUser(
  email: string,
  role: Role,
): Promise<UserCreated> {
  const { data } = await apiClient.post<UserCreated>('/api/users', {
    email,
    role,
  })
  return data
}

/**
 * Change one account's role. Separate route from the status change on purpose
 * (see the schema docstring): each request carries one field, so each of the
 * backend's guards sits unconditionally on its own path instead of behind a
 * branch selected by which keys the body happened to contain.
 */
export async function updateUserRole(
  id: string,
  role: Role,
): Promise<UserListItem> {
  const { data } = await apiClient.patch<UserListItem>(
    `/api/users/${id}/role`,
    { role },
  )
  return data
}

/**
 * Switch an account on or off. An ABSOLUTE target state, never a toggle: two
 * admins clicking at once on a toggle cancel each other out and the account
 * they both meant to disable stays live. Deactivation also revokes the target's
 * sessions server-side, so it takes effect immediately rather than whenever
 * their cookie happens to expire.
 */
export async function updateUserStatus(
  id: string,
  isActive: boolean,
): Promise<UserListItem> {
  const { data } = await apiClient.patch<UserListItem>(
    `/api/users/${id}/status`,
    { is_active: isActive },
  )
  return data
}

// ---------------------------------------------------------------------------
// The platform admin panel (card A5). EVERY route below is closed to customers
// by `require_platform_admin` on the server, on an axis disjoint from the role
// ladder — see `SessionInfo.is_platform_admin`. Nothing here is reachable with
// a tenant session however senior, and nothing here is reachable from INSIDE an
// impersonation window either, because the backend resolves that caller to the
// customer's principal.
// ---------------------------------------------------------------------------

/** A customer, as the panel lists them. */
export interface TenantAdminItem {
  id: string
  slug: string
  name: string
  status: 'active' | 'suspended'
  created_at: string
  /**
   * Accounts belonging to this customer, including deactivated ones. Zero is a
   * real and common state — a company created moments ago, before its first
   * owner exists — and the panel says so rather than hiding the row.
   */
  user_count: number
  /**
   * WHICH PRODUCT THIS CUSTOMER GETS — the profile deciding which indicators
   * their charts draw, the words their anomalies carry, whether thermal risk is
   * scored, and which rules their next pond is born with.
   *
   * An ID, so the panel has to resolve it against the catalogue to show a human
   * a name. That is deliberate on the server's side: the tenant list is one
   * query and a label would cost a join per row. Render it through
   * `listUseCaseProfiles`, never raw — a UUID in a table cell tells an operator
   * nothing about what their customer is looking at.
   *
   * NEVER NULL. The column is NOT NULL since `d5b71e40a9c3`; a `| null` here
   * would invite a branch for a state the database refuses to hold.
   */
  use_case_profile_id: string
  /**
   * Where this customer's pond map opens, or null for the world view. Read by
   * their sessions on the NEXT load, never pushed live — see `setTenantMapRegion`.
   */
  map_region: MapRegion | null
}

/**
 * One row of the use-case profile catalogue — the list of PRODUCTS this
 * installation offers, editable from the panel rather than from a deploy.
 *
 * ⚠️ ARCHIVED ROWS ARE INCLUDED, on purpose. Archiving withdraws a profile from
 * FUTURE assignment and nothing else: customers already on it keep working
 * exactly as before, so a screen that hid archived rows could not name the
 * product half its customers are on. Filter `status` when building a PICKER;
 * never when building a lookup.
 */
export interface UseCaseProfileItem {
  id: string
  /** Stable handle (`shrimp`, `tuna-farm`). Identity — it never changes. */
  key: string
  /** The label an admin reads. This is what moves when somebody tidies up. */
  label: string
  status: 'active' | 'archived'
  /** What a customer gets when nobody chose for them. Exactly one row holds it. */
  is_default: boolean
  created_at: string
  updated_at: string
  /**
   * How many indicators this profile lists, and ZERO is the state every profile
   * is born in: contents can only be added after the row exists, so "created"
   * and "usable" are different moments.
   *
   * A customer pointed at a profile with none sees no charts at all, so the
   * server refuses that assignment — the pickers filter on this figure to keep
   * an operator from meeting a refusal they cannot act on.
   */
  indicator_count: number
}

/**
 * The first owner of a customer, with a password shown exactly once.
 *
 * Same contract as `UserCreated` and for the same reason: only the Argon2 hash
 * is stored, this product has no mail delivery, and closing the dialog destroys
 * the value for everyone including us. Never log it, never put it in a store
 * that outlives the dialog, never hand it to an error reporter.
 */
export interface TenantOwnerCreated {
  id: string
  email: string
  role: Role
  created_at: string
  initial_password: string
}

/** Every customer on this installation, oldest first. */
export async function listTenants(): Promise<TenantAdminItem[]> {
  const { data } = await apiClient.get<TenantAdminItem[]>('/api/admin/tenants')
  return data
}

/**
 * Found a customer. The slug is the stable identifier and cannot be changed
 * afterwards; the name is the label and can. A duplicate slug is a 409.
 */
export async function createTenant(
  slug: string,
  name: string,
  useCaseProfileId?: string,
): Promise<TenantAdminItem> {
  const { data } = await apiClient.post<TenantAdminItem>('/api/admin/tenants', {
    slug,
    name,
    // OMITTED when nobody picked one. `TenantCreate` accepts an explicit null
    // too and collapses it to the same thing, so this is a choice about what the
    // request SAYS rather than about what it does: an absent key is "nobody
    // decided", which is the fact, and the server documents it as meaning the
    // catalogue's default.
    ...(useCaseProfileId ? { use_case_profile_id: useCaseProfileId } : {}),
  })
  return data
}

/** Rename a customer. The slug is untouched — renaming is a label change. */
export async function renameTenant(
  tenantId: string,
  name: string,
): Promise<TenantAdminItem> {
  const { data } = await apiClient.patch<TenantAdminItem>(
    `/api/admin/tenants/${tenantId}/name`,
    { name },
  )
  return data
}

/**
 * Set where a customer's pond map opens, or clear it with `null` (world view).
 *
 * TAKES EFFECT ON THEIR NEXT SESSION LOAD. The region travels on `SessionInfo`,
 * so a customer already looking at the map keeps their current view until they
 * reload or sign in again. The server rounds coordinates to five decimals and
 * answers 422 for anything outside `api/schemas/map_region.py`'s bounds.
 */
export async function setTenantMapRegion(
  tenantId: string,
  region: MapRegion | null,
): Promise<TenantAdminItem> {
  const { data } = await apiClient.patch<TenantAdminItem>(
    `/api/admin/tenants/${tenantId}/map-region`,
    { region },
  )
  return data
}

/**
 * Suspend a customer or bring them back.
 *
 * SUSPENSION IS READ-ONLY, NOT A LOCKOUT. Their people keep signing in and keep
 * seeing their history; every write and every satellite fetch is refused. Say
 * that wherever this is offered — "suspend" reads like "switch off" and the
 * difference is what somebody is deciding about.
 */
export async function setTenantStatus(
  tenantId: string,
  status: 'active' | 'suspended',
): Promise<TenantAdminItem> {
  const { data } = await apiClient.patch<TenantAdminItem>(
    `/api/admin/tenants/${tenantId}/status`,
    { status },
  )
  return data
}

/** Every product this installation offers, archived ones included. */
export async function listUseCaseProfiles(): Promise<UseCaseProfileItem[]> {
  const { data } = await apiClient.get<UseCaseProfileItem[]>(
    '/api/admin/use-case-profiles',
  )
  return data
}

/**
 * Move a customer onto a different product.
 *
 * ⚠️ IT CHANGES NOTHING ALREADY WRITTEN, and whoever offers this has to say so.
 * Alert rules already seeded onto their ponds stay — they belong to the customer
 * now and their own operator edits them — and alerts already raised keep the
 * wording frozen into them at the time. What moves is everything from here on:
 * charts, thermal scoring, and the rules the customer's NEXT pond is born with.
 * There is no backfill and no clean-up, so a farm switched from shrimp to tuna
 * keeps shrimp's rules on the ponds it already has.
 *
 * A 422 means the profile is archived or gone. Archiving withdraws a profile
 * from new assignments only; the customers on it are untouched, which is why
 * this is the one place that refusal can be made.
 */
/**
 * Add a product to the catalogue. It is born ACTIVE, ORDINARY and EMPTY.
 *
 * ⚠️ EMPTY MEANS UNUSABLE UNTIL IT HAS INDICATORS, and there is no way round
 * that: contents are written by their own routes, against a profile that must
 * already exist. Until then no customer may be put on it — the server refuses —
 * so whatever offers this has to lead somewhere that fills it in.
 *
 * `key` is identity and never changes afterwards; `label` is the text and does.
 * A duplicate key is a 409.
 */
export async function createUseCaseProfile(
  key: string,
  label: string,
): Promise<UseCaseProfileItem> {
  const { data } = await apiClient.post<UseCaseProfileItem>(
    '/api/admin/use-case-profiles',
    { key, label },
  )
  return data
}

/**
 * Change a profile's label, its status, or make it the default. One field at a
 * time — the server records a DIFFERENT audit event for each, because a relabel
 * is cosmetic while the other two change what customers get.
 *
 * ⚠️ `status: 'archived'` DOES NOT TOUCH THE CUSTOMERS ON IT. Archiving means
 * "no new assignments" and nothing else: the tenants already there keep
 * resolving it, deliberately, so that withdrawing a product cannot break the
 * people using it. Say that wherever this is offered — "archive" reads like
 * "delete" and the difference is the whole decision.
 *
 * ⚠️ `is_default: true` RE-POINTS EVERY FUTURE FOUNDING at this profile. It is
 * the highest-consequence action the catalogue offers, and there is no way to
 * un-set it: the answer to "what does a customer get when nobody chose?" cannot
 * be "nothing". Move it elsewhere instead.
 */
export async function updateUseCaseProfile(
  profileId: string,
  patch: { label?: string; status?: 'active' | 'archived'; is_default?: true },
): Promise<UseCaseProfileItem> {
  const { data } = await apiClient.patch<UseCaseProfileItem>(
    `/api/admin/use-case-profiles/${profileId}`,
    patch,
  )
  return data
}

/**
 * One indicator this BUILD can compute — the universe a profile is chosen from.
 *
 * ⚠️ NOT the same list the frontend has labels for. `INDICATOR_META` covers the
 * nine CHART indicators; the registry defines thirteen, and `PUT /indicators`
 * refuses anything outside it. Build a picker from THIS, never from the labels —
 * otherwise four indicators are silently unofferable, and an operator who names
 * one by hand meets a refusal about something the build computes fine.
 */
export interface IndicatorRegistryItem {
  key: string
  /**
   * Whether a threshold rule may name it, which is NOT the same as "a profile
   * may list it". `lst` is a real indicator a customer's charts can show, but it
   * lives outside the measurements table, so a rule on it would have nothing to
   * compare — the alert-template editor filters on this, the indicator picker
   * does not.
   */
  rule_eligible: boolean
}

/** Every indicator this build computes, in the order the product offers them. */
export async function listIndicatorRegistry(): Promise<IndicatorRegistryItem[]> {
  const { data } = await apiClient.get<IndicatorRegistryItem[]>(
    '/api/admin/use-case-profiles/indicator-registry',
  )
  return data
}

/**
 * One row of a profile's indicator list, as the catalogue EDITOR holds it.
 *
 * ⚠️ NOT `ProfileIndicator`, which is taken — that one is the session's shape,
 * keyed `key`, describing what the CURRENT tenant sees. This one is keyed
 * `indicator_key` because that is what the catalogue routes take. Named apart
 * after they silently MERGED: two interfaces of one name in one module are
 * combined by TypeScript rather than reported, so the session type quietly grew
 * a required `indicator_key` and three unrelated store tests went red.
 */
export interface ProfileIndicatorInput {
  indicator_key: string
  /**
   * The sentence shown beside an anomaly on this indicator, per direction. Both
   * optional: plenty of indicators have nothing use-case-specific to say, and
   * the alert list falls back to "<key> high vs baseline".
   */
  anomaly_high_text?: string | null
  anomaly_low_text?: string | null
}

/** The same row read back, with the position the server assigned. */
export interface ProfileIndicatorItem extends ProfileIndicatorInput {
  position: number
}

export async function getProfileIndicators(
  profileId: string,
): Promise<ProfileIndicatorItem[]> {
  const { data } = await apiClient.get<ProfileIndicatorItem[]>(
    `/api/admin/use-case-profiles/${profileId}/indicators`,
  )
  return data
}

/**
 * Replace a profile's indicator list, in the order given.
 *
 * ⚠️ POSITION COMES FROM THE ORDER OF THIS ARRAY, not from a field. The order is
 * what the admin dragged, so sending explicit positions would invite a payload
 * whose order and whose numbers disagree.
 *
 * ⚠️ THE WHOLE LIST, EVERY TIME. Anything left out is removed — and removing an
 * indicator that still carries alert templates is a 409 naming them, because the
 * templates would be left pointing at nothing.
 *
 * A 409 also answers an empty list on a profile customers depend on: they would
 * see no charts at all.
 */
export async function setProfileIndicators(
  profileId: string,
  indicators: ProfileIndicatorInput[],
): Promise<ProfileIndicatorItem[]> {
  const { data } = await apiClient.put<ProfileIndicatorItem[]>(
    `/api/admin/use-case-profiles/${profileId}/indicators`,
    indicators,
  )
  return data
}

/**
 * One threshold rule a product prescribes for a NEW pond (B12d).
 *
 * 🔴 A TEMPLATE IS NOT A RULE. It is copied into `alert_rule` once, at pond
 * creation, and the two are unrelated afterwards: an operator may retune or
 * delete their rule, and editing the template here does not reach back into any
 * pond that already exists. The editor says so on screen, because "change the
 * threshold for tuna farms" is what an admin will believe they just did.
 *
 * ⚠️ NOT EVERY INDICATOR MAY CARRY ONE — only those on `rule_eligible`. The
 * evaluator compares a stored measurement column, and `lst` has none, so a
 * template on it would never fire.
 */
export interface AlertTemplateInput {
  indicator_key: string
  comparator: 'gte' | 'lte'
  threshold: number
  level: 'watch' | 'alert' | 'critical'
  title: string
  description?: string | null
}

/**
 * The same template read back. Nothing is added — unlike an indicator, a
 * template has no server-assigned position, because the collection has no order:
 * the GET sorts by indicator, comparator and level so that re-opening the editor
 * shows the same arrangement twice running.
 */
export type AlertTemplateItem = AlertTemplateInput

export async function getProfileAlertTemplates(
  profileId: string,
): Promise<AlertTemplateItem[]> {
  const { data } = await apiClient.get<AlertTemplateItem[]>(
    `/api/admin/use-case-profiles/${profileId}/alert-templates`,
  )
  return data
}

/**
 * Replace a profile's alert templates.
 *
 * ⚠️ THE WHOLE LIST, EVERY TIME — anything left out is gone. An empty list is
 * legal and means "new ponds on this product start unwatched", which is what
 * every customer had before this card; it is NOT the refused emptiness the
 * indicator list has.
 *
 * 422 for an indicator the profile does not show, one no rule can watch, or two
 * templates sharing an indicator, direction and severity.
 */
export async function setProfileAlertTemplates(
  profileId: string,
  templates: AlertTemplateInput[],
): Promise<AlertTemplateItem[]> {
  const { data } = await apiClient.put<AlertTemplateItem[]>(
    `/api/admin/use-case-profiles/${profileId}/alert-templates`,
    templates,
  )
  return data
}

/**
 * The sixteen numbers that turn a temperature into a risk level, plus the wording
 * a customer reads with it (B12e).
 *
 * 🔴 THE ABSENCE OF THIS IS A SETTING, NOT A GAP. A product with no scoring does
 * not score thermal risk at all — the nightly run skips it while still paying for
 * the thermal ingest, and `tuna-farm` is exactly that shape. The GET therefore
 * answers `null` rather than 404; 404 is reserved for a product that is gone.
 *
 * ⚠️ NAMED `ThermalScoringInput`, not `ThermalScoring`. `ThermalObservation`,
 * `ThermalSnapshot` and friends already live in this module and describe a POND's
 * readings, not a product's tuning — and two interfaces of one name in one module
 * are MERGED by TypeScript rather than reported, which cost three unrelated store
 * specs in B12c.
 */
export interface ThermalScoringInput {
  /** Water temperature bands, °C. Must increase: low < med < high. */
  t_low: number
  t_med: number
  t_high: number
  /** Chlorophyll (NDCI) bands, unitless. Must increase, each within [-1, 1]. */
  ndci_low: number
  ndci_med: number
  ndci_high: number
  /**
   * Wind bands, m/s. ⚠️ BELOW `wind_calm_ms` THE RISK IS HIGHEST — still water
   * stratifies. The columns are named after the wind and not after the risk
   * precisely so that nobody puts the bigger number in the wrong box.
   */
  wind_calm_ms: number
  wind_moderate_ms: number
  /** Component weights. The API refuses a set that does not sum to 1. */
  w_t: number
  w_ndci: number
  w_wind: number
  /** Diurnal multipliers, each within [0, 2] — the night factor may amplify. */
  night_mult: number
  day_mult: number
  /** Cut-offs that turn the score into a label: watch < alert <= critical. */
  score_watch: number
  score_alert: number
  score_critical: number
  title: string
  description?: string | null
}

/**
 * Read back from the catalogue.
 *
 * ⚠️ THE NUMBERS COME BACK ROUNDED. These are scale-limited `Numeric` columns —
 * `t_high` is (5,2), the weights are (4,3) — so Postgres rounds what it stores and
 * both the PUT and the GET answer with what it kept, never with what was sent.
 */
export type ThermalScoringItem = ThermalScoringInput

/** The product's tuning, or `null` when it does not score thermal risk. */
export async function getProfileThermalScoring(
  profileId: string,
): Promise<ThermalScoringItem | null> {
  const { data } = await apiClient.get<ThermalScoringItem | null>(
    `/api/admin/use-case-profiles/${profileId}/thermal-scoring`,
  )
  return data
}

/**
 * Give a product a thermal risk score, or replace the one it has.
 *
 * 422 when the weights do not sum to 1 (the one rule the database does not make),
 * and 422 naming the pair when an ordering CHECK is broken.
 */
export async function setProfileThermalScoring(
  profileId: string,
  scoring: ThermalScoringInput,
): Promise<ThermalScoringItem> {
  const { data } = await apiClient.put<ThermalScoringItem>(
    `/api/admin/use-case-profiles/${profileId}/thermal-scoring`,
    scoring,
  )
  return data
}

/**
 * Stop scoring thermal risk for this kind of farm.
 *
 * ⚠️ NOT A DELETE OF SOMETHING BROKEN — it is how a product says "this species is
 * not scored". Idempotent, so removing scoring a product never had is a 204.
 */
export async function clearProfileThermalScoring(profileId: string): Promise<void> {
  await apiClient.delete(`/api/admin/use-case-profiles/${profileId}/thermal-scoring`)
}

/** What applying a product's alert rules to a customer's ponds actually did. */
export interface TenantAlertTemplatesApplied {
  /** Ponds visited — the ACTIVE ones. An archived pond is not being watched. */
  ponds: number
  /** Rules created. Routinely smaller than `ponds`, and 0 is a real answer. */
  inserted: number
  /**
   * 🔴 HOW MANY RULES THE PRODUCT PRESCRIBES, and the reason two numbers were
   * not enough. `{ponds: 1, inserted: 0}` is the answer both for a customer
   * whose ponds already carry their rules and for one whose product prescribes
   * none — and the shipped profile prescribes none, so that is what every
   * existing customer returns. Only this tells the two apart, and only one of
   * them has something to do about it.
   */
  templates: number
}

/**
 * Give a customer's EXISTING ponds the alert rules their product prescribes (B13).
 *
 * 🔴 THIS WRITES INTO THE CUSTOMER'S OWN DATA — the only admin action that does.
 * Everything else on this screen changes the company's record or its contract;
 * this reaches into `alert_rule`, which their operators own and get paged from.
 *
 * ⚠️ IT ADDS AND NEVER REPLACES. Seeding is idempotent on
 * `(pond, indicator, comparator, level)`, so a threshold their operator has
 * retuned survives, and a title edited in the catalogue does NOT propagate to
 * rules already on their ponds. Pressing it twice is safe; it is not a "sync".
 *
 * Both numbers matter: `{ponds: 12, inserted: 0}` means "they already had these",
 * and neither number alone can say that.
 */
export async function applyTenantAlertTemplates(
  tenantId: string,
): Promise<TenantAlertTemplatesApplied> {
  const { data } = await apiClient.post<TenantAlertTemplatesApplied>(
    `/api/admin/tenants/${tenantId}/alert-templates/apply`,
  )
  return data
}

export async function setTenantUseCaseProfile(
  tenantId: string,
  useCaseProfileId: string,
): Promise<TenantAdminItem> {
  const { data } = await apiClient.patch<TenantAdminItem>(
    `/api/admin/tenants/${tenantId}/use-case-profile`,
    { use_case_profile_id: useCaseProfileId },
  )
  return data
}

/**
 * Create a customer's first owner. The server generates the password and
 * returns it once — see `TenantOwnerCreated`.
 *
 * The account lands with a forced password change pending, so the person who
 * receives it sets their own on first sign-in and the value shown here stops
 * being anybody's credential.
 */
export async function createTenantOwner(
  tenantId: string,
  email: string,
): Promise<TenantOwnerCreated> {
  const { data } = await apiClient.post<TenantOwnerCreated>(
    `/api/admin/tenants/${tenantId}/owner`,
    { email },
  )
  return data
}

/**
 * Enter a customer's account.
 *
 * 204, and everything about the caller changes on the NEXT request: the backend
 * resolves them to the customer's principal, so `is_platform_admin` goes false
 * and `/api/admin/*` — including this very route — closes behind them. Whoever
 * calls this must re-read the session immediately; carrying on with the old one
 * shows a panel whose every click is now a 403.
 */
/** One security event as the panel renders it. */
export interface AuditEntry {
  id: number
  occurred_at: string
  event_type: string
  outcome: 'allowed' | 'denied'
  actor_email: string | null
  actor_role: Role | null
  target_email: string | null
  ip: string | null
  correlation_id: string | null
  detail: Record<string, unknown> | null
}

export interface AuditPage {
  tenant_id: string
  items: AuditEntry[]
  /**
   * Hand it back verbatim to get the next page, or null when there is none.
   *
   * OPAQUE ON PURPOSE: it encodes the `(occurred_at, id)` pair the keyset query
   * resumes from, and a client that parses it breaks the day the ordering gains
   * a third column. A null is measured by the server fetching one row more than
   * asked for, not inferred from a short page.
   */
  next_cursor: string | null
}

/**
 * One page of a customer's security trail, newest first.
 *
 * ⚠️ FAILED LOGINS ARE NOT IN IT, and no parameter can add them.
 * `login_failed` carries no tenant because its only identifier is the address
 * somebody typed — resolving that to a customer would let whoever is making the
 * attempts choose whose screen their attempts appear on. Whatever renders this
 * must say so, or it implies a completeness the data does not have.
 */
export async function fetchTenantAudit(
  tenantId: string,
  options: { limit?: number; cursor?: string | null } = {},
): Promise<AuditPage> {
  const { data } = await apiClient.get<AuditPage>(
    `/api/admin/tenants/${tenantId}/audit`,
    { params: { limit: options.limit ?? 50, cursor: options.cursor ?? undefined } },
  )
  return data
}

export async function startImpersonation(tenantId: string): Promise<void> {
  await apiClient.post(`/api/admin/tenants/${tenantId}/impersonate`)
}

/**
 * Leave the customer's account and become a platform admin again. 204.
 *
 * 🔴 UNDER /api/auth, NOT /api/admin, AND THAT IS THE WHOLE POINT. Inside a
 * window every admin route is shut to the one person who needs out, so the exit
 * lives beside logout. It is NOT logout: signing out and back in would end the
 * window too, at the cost of the operator's own session and a fresh password
 * prompt for something that should be one click.
 *
 * Idempotent — a caller who was not impersonating gets the state they asked for.
 */
export async function stopImpersonation(): Promise<void> {
  await apiClient.post('/api/auth/impersonation/stop')
}

/**
 * One flag's state for one tenant, as the admin panel draws it. Mirrors
 * `TenantFeatureItem` in `api/schemas/admin_tenants.py`.
 *
 * `enabled` is the EFFECTIVE answer, so the panel never has to know the
 * catalogue defaults to render a switch. `is_override` is what it needs on top:
 * a tenant on the standard product and one explicitly set to the same value
 * look identical through `enabled` alone, and only the second is a decision
 * somebody made.
 */
export interface TenantFeatureItem {
  /**
   * `string`, NOT `FeatureFlag`, and the looseness is the honest type.
   *
   * The whole point of the server owning the catalogue is that it can send a
   * module this build has never heard of. Narrowing to the three-value union
   * here would have TypeScript insist that cannot happen, and the next person
   * would write `FEATURE_LABELS[item.feature].label` without a guard — an
   * `undefined.label` in production, on the exact day the design was supposed
   * to pay off. `setTenantFeature` below still takes the union, because THERE
   * the caller knows the flag statically.
   */
  feature: string
  enabled: boolean
  is_override: boolean
}

/**
 * Every flag in the catalogue for one tenant — the whole list, not just the
 * overrides.
 *
 * THE SERVER OWNS THE CATALOGUE. The panel renders one switch per item it
 * receives, so a fourth module appears here with no frontend change at all. Do
 * not "optimise" this into a local list crossed with the overrides: that moves
 * the catalogue into this codebase, where it silently keeps working while
 * showing the wrong number of switches.
 */
export async function listTenantFeatures(tenantId: string): Promise<TenantFeatureItem[]> {
  const { data } = await apiClient.get<TenantFeatureItem[]>(
    `/api/admin/tenants/${tenantId}/features`,
  )
  return data
}

/**
 * Switch one module on or off for one customer. Returns the new state.
 *
 * `backfillHistory` additionally asks the server to pull each pond's history for
 * the module; the work runs after the response, so `backfill_queued` is a count
 * of ponds STARTED, not finished — and it is 0 whenever nothing was asked for or
 * the customer has no active ponds.
 */
export async function setTenantFeature(
  tenantId: string,
  feature: FeatureFlag,
  enabled: boolean,
  backfillHistory = false,
): Promise<TenantFeatureItem & { backfill_queued: number }> {
  const { data } = await apiClient.put<TenantFeatureItem & { backfill_queued: number }>(
    `/api/admin/tenants/${tenantId}/features/${feature}`,
    { enabled, backfill_history: backfillHistory },
  )
  return data
}
